import React, { useState, useCallback, useMemo } from 'react';
import { ButtonGroup, Skeleton, Typography } from '@scale/scaleui';
import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core';
import _ from 'lodash';
import {
  Attachment,
  AttachmentSource,
  ModelInputFileAttachmentType,
} from '@scale/shared/types/chat';
import { useQuery } from 'react-query';
import apiFetch from 'react-scale/lib/api-fetch';
import ImageMetadataTags from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/FocusedImageModal/ImageMetadataTags';
import FocusedImageModal from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/FocusedImageModal/FocusedImageModal';
import { MultimodalAssetDto } from '@scale/mm-shared';
import { twMerge } from 'tailwind-merge';
import { getAudioSource } from 'react-scale/lib/chat-api/multimodal';

const SCALE_S3_PREFIX = 's3://scale';
const SCALE_DATA_PREFIX = 'scaledata:';
const SCALE_CDS_PREFIX = 'scale-cds://';

interface AttachmentCarouselProps {
  index: number;
  setIndex: (index: number) => void;
  attachments: Attachment[];
  deleteAttachment: (index: number) => void;
  readOnly: boolean;
  imagesToMetadata: Record<string, MultimodalAssetDto>;
}

const ChatAttachmentCarousel: React.FC<AttachmentCarouselProps> = ({
  index,
  setIndex,
  attachments,
  deleteAttachment,
  readOnly,
  imagesToMetadata,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  // Sets to null on index changes
  const currentAttachment: Attachment | null = useMemo(() => {
    return attachments[index] || null;
  }, [attachments, index]);

  const emptyUrl = !currentAttachment?.url || currentAttachment?.url === '';

  const isScaleInternalFile =
    currentAttachment?.url?.startsWith(SCALE_S3_PREFIX) ||
    currentAttachment?.url?.startsWith(SCALE_DATA_PREFIX) ||
    currentAttachment?.url?.startsWith(SCALE_CDS_PREFIX);

  const onLeft = useCallback(() => {
    setIndex(_.clamp(index - 1, 0, attachments.length - 1));
  }, [attachments.length, index, setIndex]);

  const onRight = useCallback(() => {
    setIndex(_.clamp(index + 1, 0, attachments.length - 1));
  }, [attachments.length, index, setIndex]);

  const { data: fileFetchData, isError: queryError } = useQuery(
    ['chat-attachment', currentAttachment?.url, currentAttachment?.originalUrl],
    async (): Promise<{ url: string } | undefined> => {
      if (!currentAttachment?.url) {
        return undefined;
      }

      // only fetch the file if it's a Scale S3 file
      if (isScaleInternalFile) {
        // we have a special function get get audio files
        // this is currently just to avoid fetching stale data from the CDN
        if (currentAttachment?.fileType === ModelInputFileAttachmentType.Audio) {
          const { source } = await getAudioSource({ audioUrl: currentAttachment.url });
          return { url: source };
        }

        /**
         * In the case that the attachment is mirrored from an external source (this is the case if currentAttachment.originalUrl is defined),
         * and the assert is mirrored to CDS, we need to check if the mirrored asset (i.e currentAttachment.url) is expired.
         * If it is, then we should try to use the original asset.
         */
        if (currentAttachment.url?.startsWith(SCALE_CDS_PREFIX) && currentAttachment.originalUrl) {
          const assetExpirationResponse = await apiFetch<{ isExpired: boolean }>(
            null,
            `internal/contentUnderstanding/getCDSAssetExpiration`,
            {
              method: 'GET',
              query: { assetUrl: currentAttachment.url },
            },
          );

          if (assetExpirationResponse.isExpired) {
            const isOriginalUrlAnInternalFile =
              currentAttachment?.originalUrl?.startsWith(SCALE_S3_PREFIX) ||
              currentAttachment?.originalUrl?.startsWith(SCALE_DATA_PREFIX) ||
              currentAttachment?.originalUrl?.startsWith(SCALE_CDS_PREFIX);

            if (isOriginalUrlAnInternalFile) {
              return await apiFetch<{ url: string }>(
                null,
                `internal/contentUnderstanding/fileFetch`,
                {
                  method: 'POST',
                  body: { url: currentAttachment.originalUrl },
                },
              );
            }

            return { url: currentAttachment.originalUrl };
          }
        }

        return await apiFetch<{ url: string }>(null, `internal/contentUnderstanding/fileFetch`, {
          method: 'POST',
          body: { url: currentAttachment.url },
        });
      }

      return { url: currentAttachment.url };
    },
    {
      staleTime: 23 * 60 * 60 * 1_000, // 23 hours
      enabled: !!currentAttachment?.url,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  );

  const isError = queryError || emptyUrl;

  const currentImageWithMetadata:
    | (Partial<MultimodalAssetDto> & { image_url: string; source_url?: string })
    | undefined = useMemo(() => {
    // if the current attachment is an image with a url, this is the full multimodal image asset with metadata for the current attachment if available, else just the (original) url, else undefined
    if (
      currentAttachment.fileType === ModelInputFileAttachmentType.Image &&
      currentAttachment.url
    ) {
      const maybeImageWithMetadata = imagesToMetadata[currentAttachment.url];
      if (maybeImageWithMetadata) return maybeImageWithMetadata;
      const imageSrc = currentAttachment.base64EncodedFileBytes
        ? `data:image/png;base64,${currentAttachment.base64EncodedFileBytes}`
        : fileFetchData?.url.length && isScaleInternalFile
        ? (fileFetchData.url as string)
        : currentAttachment.originalUrl ?? currentAttachment.url;

      return { image_url: imageSrc, source_url: currentAttachment.sourceUrl };
    } else {
      return undefined;
    }
  }, [currentAttachment, fileFetchData, imagesToMetadata, isScaleInternalFile]);

  const currentImageSubmissionMethod: AttachmentSource | undefined = useMemo(() => {
    if (currentAttachment.imageSubmissionMethod) return currentAttachment.imageSubmissionMethod;
    if (!currentImageWithMetadata || !currentAttachment.url) return undefined;
    // use image_category field existence as a proxy for imgae coming from the DB
    if (currentImageWithMetadata.image_category) return AttachmentSource.MM_IMAGE_SERVICE;
    if (
      [SCALE_S3_PREFIX, SCALE_CDS_PREFIX].some(prefix => currentAttachment.url?.startsWith(prefix))
    )
      return AttachmentSource.FILE_UPLOAD;
    return AttachmentSource.URL;
  }, [currentImageWithMetadata, currentAttachment.url, currentAttachment.imageSubmissionMethod]);

  const buttons: {
    icon: [IconPrefix, IconName];
    label: string;
    labelHidden: boolean;
    onClick: () => void;
  }[] = [
    {
      icon: ['fas', 'arrow-left'],
      label: 'previous',
      labelHidden: true,
      onClick: () => {
        onLeft();
      },
    },
    {
      icon: ['fas', 'arrow-right'],
      label: 'next',
      labelHidden: true,
      onClick: () => {
        onRight();
      },
    },
    {
      icon: isExpanded ? ['fas', 'compress-alt'] : ['fas', 'expand-alt'],
      label: 'Expand',
      labelHidden: true,
      onClick: () => setIsExpanded(!isExpanded),
    },
  ];

  if (!readOnly) {
    buttons.push({
      icon: ['fas', 'trash'],
      label: 'Delete',
      labelHidden: true,
      onClick: () => deleteAttachment(index),
    });
  }

  const CurrentImageCard = useMemo(() => {
    if (
      currentAttachment.fileType === ModelInputFileAttachmentType.Image &&
      currentImageWithMetadata
    ) {
      const imageSrc = currentImageWithMetadata.image_url;

      return (
        <div
          className={twMerge('gap-2 flex flex-col', isExpanded ? 'max-w-full max-h-full' : 'w-1/2')}
        >
          <img
            src={imageSrc}
            alt={currentAttachment.name}
            onClick={() => setIsFocused(true)}
            className="cursor-pointer"
          />
          {currentImageWithMetadata && <ImageMetadataTags image={currentImageWithMetadata} />}
          <Typography.Overline2>
            Image added via {currentImageSubmissionMethod}{' '}
          </Typography.Overline2>
          {currentImageSubmissionMethod !== AttachmentSource.FILE_UPLOAD &&
            !isScaleInternalFile && (
              <div>
                <Typography.Overline2>Image URL:</Typography.Overline2>
                <Typography.Caption className="break-words text-primary-400 hover:text-primary-600">
                  <a href={imageSrc}>{imageSrc}</a>
                </Typography.Caption>
                {currentImageWithMetadata.source_url && (
                  <>
                    <Typography.Overline2>Source URL:</Typography.Overline2>
                    <Typography.Caption className="break-words text-primary-400 hover:text-primary-600">
                      <a
                        href={currentImageWithMetadata.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {currentImageWithMetadata.source_url}
                      </a>
                    </Typography.Caption>
                  </>
                )}
              </div>
            )}
        </div>
      );
    }

    if (currentAttachment.fileType === ModelInputFileAttachmentType.Audio) {
      return fileFetchData === undefined ? (
        <Skeleton className="animate-pulse" />
      ) : (
        <audio src={fileFetchData.url} controls style={{ width: '95%' }} />
      );
    }

    return <div>Visualization not yet supported for type: {currentAttachment.fileType}</div>;
  }, [
    currentAttachment.fileType,
    currentAttachment.name,
    currentImageWithMetadata,
    isExpanded,
    currentImageSubmissionMethod,
    isScaleInternalFile,
    fileFetchData,
  ]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row items-center">
        <ButtonGroup buttons={attachments.length > 1 ? buttons : buttons.slice(2)} />
        <h1 className="ml-2 text-m font-medium text-neutral-900">Attachment {index + 1}</h1>
      </div>
      {currentAttachment && (
        <div>
          {isError && <div>Error fetching file</div>}
          {!isError && CurrentImageCard}
        </div>
      )}
      {isFocused && currentImageWithMetadata && (
        <FocusedImageModal image={currentImageWithMetadata} onCancel={() => setIsFocused(false)} />
      )}
    </div>
  );
};

export default ChatAttachmentCarousel;
